import type { ApiOk, ApiResponse } from '@lms/shared-types'

export interface PageMeta {
  total: number
  page: number
  limit: number
  // Cursor-pagination fields — populated when the route used ?before=
  hasMore?: boolean
  nextCursor?: string | null
}

export interface PagedResult<T> {
  data: T[]
  meta: PageMeta
}

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')

// ── View-as (read-only HR+ scope impersonation) ──────────────────────────
// Module-level state read by every request. The
// EffectiveEmployeeProvider calls `setViewAsEmployeeId(id|null)` when the
// user enters or exits "Viewing As" mode. The header is honoured by the
// backend `effective_actor` dependency for read-scoped GET handlers only.
// Mutations always attribute to the real actor regardless of this header.
let _viewAsEmployeeId: string | null = null

export function setViewAsEmployeeId(id: string | null): void {
  _viewAsEmployeeId = id
}

export function getViewAsEmployeeId(): string | null {
  return _viewAsEmployeeId
}

export class ApiError extends Error {
  public readonly status: number
  public readonly details: unknown

  constructor(message: string, status: number, details: unknown = null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT'
  body?: unknown
  signal?: AbortSignal
  headers?: Record<string, string>
}

function buildUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  return `${API_BASE}${normalized}`
}

function isApiResponse<T>(value: unknown): value is ApiResponse<T> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'success' in value &&
    typeof (value as { success: unknown }).success === 'boolean'
  )
}

/** E1: on a 401 mid-session (expired/cleared cookie), send the user to login
 *  instead of letting data silently stop loading. Skips `/auth/*` (the session
 *  probe legitimately 401s when logged out — auth-context handles that) and
 *  avoids a loop when already on /login. */
function redirectToLoginOn401(path: string, status: number): void {
  if (status !== 401 || path.includes('/auth/')) return
  if (typeof window === 'undefined') return
  if (window.location.pathname.startsWith('/login')) return
  const next = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.assign(`/login?next=${next}`)
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, signal, headers = {} } = options

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(_viewAsEmployeeId ? { 'X-View-As': _viewAsEmployeeId } : {}),
      ...headers,
    },
    ...(signal ? { signal } : {}),
  }

  if (body !== undefined) {
    init.body = JSON.stringify(body)
  }

  const response = await fetch(buildUrl(path), init)

  let payload: unknown = null
  const text = await response.text()
  if (text.length > 0) {
    try {
      payload = JSON.parse(text) as unknown
    } catch {
      payload = text
    }
  }

  if (!response.ok) {
    redirectToLoginOn401(path, response.status)
    const message =
      isApiResponse<T>(payload) && !payload.success
        ? payload.error
        : `Request failed with status ${response.status}`
    throw new ApiError(message, response.status, payload)
  }

  if (isApiResponse<T>(payload)) {
    if (!payload.success) {
      throw new ApiError(payload.error, response.status, payload.details)
    }
    return payload.data
  }

  // Non-envelope response (e.g. 204) — return as-is when caller expects it.
  return payload as T
}

/** Variant of `apiRequest` that preserves the `meta` block alongside
 *  `data`. Use for paged endpoints (`OkPaged[T]` on the API side) so
 *  the caller can drive `useInfiniteQuery` with `total` / `page` /
 *  `limit`. */
export async function apiRequestPaged<T>(
  path: string,
  options: RequestOptions = {},
): Promise<PagedResult<T>> {
  const { method = 'GET', body, signal, headers = {} } = options

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(_viewAsEmployeeId ? { 'X-View-As': _viewAsEmployeeId } : {}),
      ...headers,
    },
    ...(signal ? { signal } : {}),
  }
  if (body !== undefined) init.body = JSON.stringify(body)

  const response = await fetch(buildUrl(path), init)
  let payload: unknown = null
  const text = await response.text()
  if (text.length > 0) {
    try {
      payload = JSON.parse(text) as unknown
    } catch {
      payload = text
    }
  }

  if (!response.ok) {
    redirectToLoginOn401(path, response.status)
    const message =
      isApiResponse<T[]>(payload) && !payload.success
        ? payload.error
        : `Request failed with status ${response.status}`
    throw new ApiError(message, response.status, payload)
  }

  if (!isApiResponse<T[]>(payload) || !payload.success) {
    throw new ApiError(
      'Expected paged envelope, got unexpected payload',
      response.status,
      payload,
    )
  }

  const ok = payload as ApiOk<T[]>
  if (!ok.meta) {
    throw new ApiError(
      'Paged endpoint returned envelope without meta block',
      response.status,
      payload,
    )
  }
  return { data: ok.data, meta: ok.meta }
}
